import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Download, Trash2, User, ShieldCheck, Cookie, Mail, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/* Authenticated Privacy & Data dashboard. Every action here maps to a
 * scoped endpoint under /api/privacy — nothing crosses tenants. */
export default function PrivacyDataDashboard() {
    const [me, setMe] = useState(null);
    const [acceptances, setAcceptances] = useState([]);
    const [connected, setConnected] = useState({ biometric: [] });
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [password, setPassword] = useState("");
    const [confirmation, setConfirmation] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/privacy/me").then((r) => setMe(r.data)).catch(() => {});
        api.get("/privacy/consent-history").then((r) => setAcceptances(r.data?.acceptances || [])).catch(() => {});
        api.get("/privacy/connected-services").then((r) => setConnected(r.data || { biometric: [] })).catch(() => {});
    }, []);

    const downloadExport = async () => {
        setBusy(true);
        try {
            const r = await api.get("/privacy/export");
            const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `vault-export-${new Date().toISOString().slice(0, 10)}.json`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Your data export has been downloaded.");
        } catch (e) {
            toast.error("Export failed. Please try again.");
        } finally { setBusy(false); }
    };

    const deleteAccount = async () => {
        setBusy(true);
        try {
            await api.post("/privacy/delete-account", { password, confirmation });
            toast.success("Deletion requested. Signing out.");
            setTimeout(() => { window.location.href = "/"; }, 1500);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Deletion failed.");
        } finally { setBusy(false); }
    };

    return (
        <div className="max-w-4xl mx-auto p-6 md:p-8" data-testid="privacy-data-dashboard">
            <div className="mb-4 flex items-center gap-2 text-xs">
                <Link to="/dashboard" className="text-stone-500 hover:text-stone-900 flex items-center gap-1">
                    <ArrowLeft size={12} /> Dashboard
                </Link>
                <span className="text-stone-300">/</span>
                <span className="font-medium">Privacy &amp; Data</span>
            </div>
            <div className="flex items-start gap-3 mb-6">
                <div className="mt-1 rounded-full bg-blue-50 p-1.5"><ShieldCheck size={16} className="text-blue-600" /></div>
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Privacy &amp; Data</h1>
                    <p className="text-sm text-stone-500 mt-1 max-w-xl">
                        View, export or delete your personal information. Every action here is scoped to your account
                        only and audit-logged for your own records.
                    </p>
                </div>
            </div>

            {/* My information */}
            <section className="rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 p-5" data-testid="pd-me">
                <div className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-3 flex items-center gap-1.5"><User size={12} /> My information</div>
                {me ? (
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <div><dt className="text-xs text-stone-500">Name</dt><dd className="font-medium">{me.name || "—"}</dd></div>
                        <div><dt className="text-xs text-stone-500">Email</dt><dd className="font-medium">{me.email}</dd></div>
                        <div><dt className="text-xs text-stone-500">Role</dt><dd className="font-medium">{me.role || "—"}</dd></div>
                        <div><dt className="text-xs text-stone-500">Country</dt><dd className="font-medium">{me.country || "—"}</dd></div>
                    </dl>
                ) : <div className="text-sm text-stone-400">Loading…</div>}
            </section>

            {/* Data export */}
            <section className="mt-4 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 p-5" data-testid="pd-export">
                <div className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2 flex items-center gap-1.5"><Download size={12} /> Download my data</div>
                <p className="text-sm text-stone-600 mb-3 max-w-xl">
                    Export everything VAULT holds about you — profile, preferences, cards, expenses, bills, assets,
                    documents metadata, WebAuthn credentials and legal acceptances — in a single JSON file.
                </p>
                <Button onClick={downloadExport} disabled={busy} data-testid="pd-export-btn">
                    <Download size={13} className="mr-1.5" /> Download JSON export
                </Button>
            </section>

            {/* Consent history */}
            <section className="mt-4 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 p-5" data-testid="pd-consents">
                <div className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-3 flex items-center gap-1.5"><FileText size={12} /> Consent history</div>
                {acceptances.length === 0 ? (
                    <div className="text-sm text-stone-400">No consent records yet.</div>
                ) : (
                    <ul className="space-y-1.5">
                        {acceptances.map((a) => (
                            <li key={a.id} className="text-xs flex items-center justify-between gap-3 rounded-md border border-stone-100 dark:border-stone-800 px-3 py-1.5">
                                <span className="font-mono">{a.slug}</span>
                                <span className="text-stone-500">{a.accepted_at?.slice(0, 19).replace("T", " ")}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* Connected services */}
            <section className="mt-4 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 p-5" data-testid="pd-connected">
                <div className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-3">Connected services</div>
                {connected.biometric?.length === 0 ? (
                    <div className="text-sm text-stone-400">No biometric credentials registered.</div>
                ) : (
                    <ul className="space-y-1.5">
                        {connected.biometric.map((b) => (
                            <li key={b.id} className="text-xs flex items-center justify-between gap-3 rounded-md border border-stone-100 px-3 py-1.5">
                                <span>{b.device_label}</span>
                                <span className="text-stone-500">{b.created_at?.slice(0, 10)}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* Cookies + contact */}
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <Link to="/cookies" className="rounded-xl border border-stone-200 bg-white p-5 hover:border-amber-400 transition" data-testid="pd-cookies">
                    <div className="flex items-center gap-2 text-sm font-semibold"><Cookie size={13} /> Cookie preferences</div>
                    <div className="text-xs text-stone-500 mt-1">Manage functional, analytics and marketing cookies.</div>
                </Link>
                <a href="mailto:privacy@vaultbypd.com" className="rounded-xl border border-stone-200 bg-white p-5 hover:border-amber-400 transition" data-testid="pd-contact">
                    <div className="flex items-center gap-2 text-sm font-semibold"><Mail size={13} /> Contact the privacy team</div>
                    <div className="text-xs text-stone-500 mt-1">privacy@vaultbypd.com · replies within one business day.</div>
                </a>
            </div>

            {/* Delete account */}
            <section className="mt-6 rounded-xl border border-rose-200 bg-rose-50/40 dark:bg-rose-950/20 p-5" data-testid="pd-delete">
                <div className="text-xs font-semibold uppercase tracking-wider text-rose-700 mb-2 flex items-center gap-1.5"><Trash2 size={12} /> Delete account</div>
                <p className="text-sm text-stone-700 dark:text-stone-300 mb-3 max-w-xl">
                    Requesting deletion removes personal identifiers within 30 days. Anonymised transactional records
                    may be retained for statistical use. Encrypted backups rotate out over 30 days.
                </p>
                {!deleteOpen ? (
                    <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)} data-testid="pd-delete-open">
                        Request account deletion
                    </Button>
                ) : (
                    <div className="space-y-2 max-w-md">
                        <Input type="password" placeholder="Your password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="pd-delete-password" />
                        <Input placeholder="Type DELETE MY ACCOUNT to confirm" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} data-testid="pd-delete-confirm" />
                        <div className="flex gap-2">
                            <Button variant="destructive" size="sm" disabled={busy} onClick={deleteAccount} data-testid="pd-delete-submit">
                                {busy ? "Requesting…" : "Delete my account"}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => { setDeleteOpen(false); setPassword(""); setConfirmation(""); }}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}
